import { useMemo, useState } from "react";
import { Card, Checkbox } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const FLAGS = [
  { key: "g", labelKey: "regex_tester_flag_g" },
  { key: "i", labelKey: "regex_tester_flag_i" },
  { key: "m", labelKey: "regex_tester_flag_m" },
  { key: "s", labelKey: "regex_tester_flag_s" },
  { key: "u", labelKey: "regex_tester_flag_u" },
  { key: "y", labelKey: "regex_tester_flag_y" },
];

const PRESETS = [
  {
    key: "email",
    pattern: "[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}",
  },
  {
    key: "url",
    pattern: "https?:\\/\\/[^\\s/$.?#][^\\s]*",
  },
  {
    key: "ipv4",
    pattern:
      "\\b(?:(?:25[0-5]|2[0-4]\\d|1?\\d?\\d)\\.){3}(?:25[0-5]|2[0-4]\\d|1?\\d?\\d)\\b",
  },
  {
    key: "uuid",
    pattern:
      "\\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\\b",
  },
  {
    key: "us_phone",
    pattern: "\\(?\\d{3}\\)?[-.\\s]?\\d{3}[-.\\s]?\\d{4}",
  },
  {
    key: "hex_color",
    pattern: "#(?:[0-9a-fA-F]{3}){1,2}\\b",
  },
  {
    key: "iso_date",
    pattern: "\\b\\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\\d|3[01])\\b",
  },
  {
    key: "slug",
    pattern: "\\b[a-z0-9]+(?:-[a-z0-9]+)*\\b",
  },
];

const DEFAULT_PATTERN = "(\\w+)@(\\w+\\.\\w+)";
const DEFAULT_FLAGS = "g";
const DEFAULT_TEST =
  "Contact alice@example.com or bob@quto.dev for details. Backup: carol@test.io.";
const DEFAULT_REPLACEMENT = "[email: $1 at $2]";

const buildRegex = (pattern, flags) => {
  if (!pattern) return { regex: null, error: null };
  try {
    return { regex: new RegExp(pattern, flags), error: null };
  } catch (e) {
    return { regex: null, error: e.message };
  }
};

const collectMatches = (regex, input) => {
  if (!regex || !input) return [];
  const matches = [];
  if (regex.global || regex.sticky) {
    let m;
    let safety = 0;
    const re = new RegExp(regex.source, regex.flags);
    while ((m = re.exec(input)) !== null) {
      matches.push({
        match: m[0],
        index: m.index,
        groups: m.slice(1),
        named: m.groups || null,
      });
      if (m.index === re.lastIndex) re.lastIndex += 1;
      safety += 1;
      if (safety > 10000) break;
    }
  } else {
    const m = regex.exec(input);
    if (m) {
      matches.push({
        match: m[0],
        index: m.index,
        groups: m.slice(1),
        named: m.groups || null,
      });
    }
  }
  return matches;
};

const renderHighlighted = (input, matches) => {
  if (!matches.length) return input;
  const parts = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.index > cursor) {
      parts.push(<span key={`t-${i}`}>{input.slice(cursor, m.index)}</span>);
    }
    parts.push(
      <mark
        key={`m-${i}`}
        className="bg-yellow-200 text-gray-900 rounded px-0.5"
      >
        {input.slice(m.index, m.index + m.match.length)}
      </mark>
    );
    cursor = m.index + m.match.length;
    if (m.match.length === 0) cursor = m.index + 1;
  });
  if (cursor < input.length) {
    parts.push(<span key="tail">{input.slice(cursor)}</span>);
  }
  return parts;
};

const RegexTester = () => {
  const { t } = useTranslation();
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [testText, setTestText] = useState(DEFAULT_TEST);
  const [replacement, setReplacement] = useState(DEFAULT_REPLACEMENT);

  const toggleFlag = (flag) => {
    setFlags((prev) =>
      prev.includes(flag) ? prev.replace(flag, "") : prev + flag
    );
  };

  const applyPreset = (preset) => {
    setPattern(preset.pattern);
    setFlags((prev) => (prev.includes("g") ? prev : prev + "g"));
  };

  const { regex, error } = useMemo(
    () => buildRegex(pattern, flags),
    [pattern, flags]
  );

  const matches = useMemo(
    () => collectMatches(regex, testText),
    [regex, testText]
  );

  const replaced = useMemo(() => {
    if (!regex || error) return null;
    try {
      return testText.replace(regex, replacement);
    } catch (e) {
      return null;
    }
  }, [regex, error, testText, replacement]);

  const copy = async (text, label) => {
    if (text === null || text === undefined || text === "") {
      toast.error(t("regex_tester_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("regex_tester_title")}
            </h1>
            <p className="text-gray-600">{t("regex_tester_intro")}</p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("regex_tester_pattern_label")}
              </label>
              <div className="flex items-stretch font-mono text-sm border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-indigo-400 overflow-hidden">
                <span className="px-2 flex items-center bg-gray-100 text-gray-500 select-none">
                  /
                </span>
                <input
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  placeholder={t("regex_tester_pattern_placeholder")}
                  className="flex-1 p-2 outline-none"
                />
                <span className="px-2 flex items-center bg-gray-100 text-gray-500 select-none">
                  /
                </span>
                <span className="px-2 flex items-center bg-gray-100 text-indigo-700 font-semibold select-none">
                  {flags || "—"}
                </span>
              </div>
              {error && (
                <div className="text-xs text-red-600 mt-1">
                  {t("regex_tester_invalid")}: {error}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("regex_tester_presets_label")}
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => {
                  const active = pattern === p.pattern;
                  return (
                    <StandardButton
                      key={p.key}
                      onClick={() => applyPreset(p)}
                      type={active ? "primary" : "secondary"}
                      variant={active ? undefined : "outlined"}
                      size="small"
                    >
                      {t(`regex_tester_preset_${p.key}`)}
                    </StandardButton>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("regex_tester_flags_label")}
              </label>
              <div className="flex flex-wrap gap-3">
                {FLAGS.map((f) => (
                  <Checkbox
                    key={f.key}
                    checked={flags.includes(f.key)}
                    onChange={() => toggleFlag(f.key)}
                  >
                    <span className="font-mono">{f.key}</span>{" "}
                    <span className="text-xs text-gray-500">
                      — {t(f.labelKey)}
                    </span>
                  </Checkbox>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("regex_tester_test_label")}
              </label>
              <textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder={t("regex_tester_test_placeholder")}
                rows={5}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
              <div className="flex items-center justify-between mb-2">
                <strong className="text-indigo-700">
                  {t("regex_tester_highlight_label")}
                </strong>
                <span className="text-xs text-gray-600">
                  {t("regex_tester_match_count", { count: matches.length })}
                </span>
              </div>
              <pre className="bg-white p-3 rounded border border-indigo-100 text-sm whitespace-pre-wrap break-all min-h-[3rem]">
                {regex && !error ? (
                  renderHighlighted(testText, matches)
                ) : (
                  <span className="text-gray-400">
                    {t("regex_tester_no_pattern")}
                  </span>
                )}
              </pre>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
              <strong className="text-indigo-700 block mb-2">
                {t("regex_tester_matches_label")}
              </strong>
              {matches.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {t("regex_tester_no_matches")}
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-indigo-100 max-h-72 overflow-auto">
                  {matches.map((m, idx) => (
                    <li key={`${idx}-${m.index}`} className="py-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-mono text-sm break-all">
                          <span className="text-xs text-indigo-700 mr-2">
                            #{idx + 1} @ {m.index}
                          </span>
                          <span className="text-gray-900">{m.match}</span>
                        </span>
                        <StandardButton
                          onClick={() =>
                            copy(m.match, t("regex_tester_match_label"))
                          }
                          type="secondary"
                          variant="outlined"
                          size="small"
                        >
                          {t("copy_button")}
                        </StandardButton>
                      </div>
                      {(m.groups.length > 0 || m.named) && (
                        <div className="mt-1 ml-6 text-xs text-gray-600 font-mono">
                          {m.groups.map((g, gi) => (
                            <div key={`g-${gi}`}>
                              <span className="text-indigo-600">
                                ${gi + 1}
                              </span>
                              : {g === undefined ? "—" : g}
                            </div>
                          ))}
                          {m.named &&
                            Object.entries(m.named).map(([name, val]) => (
                              <div key={`n-${name}`}>
                                <span className="text-indigo-600">
                                  ?&lt;{name}&gt;
                                </span>
                                : {val === undefined ? "—" : val}
                              </div>
                            ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("regex_tester_replacement_label")}
              </label>
              <input
                value={replacement}
                onChange={(e) => setReplacement(e.target.value)}
                placeholder={t("regex_tester_replacement_placeholder")}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              />
              <div className="bg-indigo-50 p-3 rounded-md border border-indigo-200 mt-1">
                <div className="flex items-center justify-between mb-1">
                  <strong className="text-indigo-700 text-sm">
                    {t("regex_tester_replaced_label")}
                  </strong>
                  <StandardButton
                    onClick={() =>
                      copy(replaced || "", t("regex_tester_replaced_label"))
                    }
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!replaced}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
                <pre className="bg-white p-2 rounded border border-indigo-100 text-sm whitespace-pre-wrap break-all min-h-[2rem]">
                  {replaced !== null ? (
                    replaced
                  ) : (
                    <span className="text-gray-400">
                      {t("regex_tester_no_pattern")}
                    </span>
                  )}
                </pre>
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("regex_tester_note_engine")}</p>
              <p>{t("regex_tester_note_replace")}</p>
              <p>{t("regex_tester_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default RegexTester;
